/**
 * The marketing page.
 *
 * One primary action per section and never two competing ones: the dark button
 * is always "start planning". The sections answer the questions in the order
 * they get asked: what is it, what is it for, how does it work, what does it
 * do, what do I get on paper, is it really free, and only then the objections —
 * followed by an address for everything the FAQ didn't answer. That address is
 * a text link, not a button: the dark button stays "start planning".
 */

import { motion, useReducedMotion } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'
import { type MouseEvent, useEffect, useState } from 'react'

import { BrandMark } from '../components/TopBar'
import { LanguagePicker } from '../components/LanguagePicker'
import { NewPlanDialog } from '../components/NewPlanDialog'
import { RoomThumb } from '../components/RoomThumb'
import { ThemeSwitch } from '../components/ThemeSwitch'
import { useCreatePlan } from '../hooks/useCreatePlan'
import { useLocale, useT } from '../lib/i18n/I18nProvider'
import { messageForError } from '../lib/i18n/errors'
import { screenSwatchFor } from '../lib/colors'
import { EVENT_KINDS } from '../lib/events'
import { computeTableGeometry } from '../lib/geometry'
import { planWords } from '../lib/planWords'
import type { EventKind, TableColor, TableShape } from '../lib/types'
import { LOCALES, LOCALE_NAMES, type LocaleCode } from '../lib/i18n'
import { landingPath } from '../lib/i18n/paths'
import { api } from '../lib/api'
import { CONTACT_EMAIL } from '../lib/config'
import { claimStaticLanding } from '../lib/prerender'

/**
 * Section reveals. Deliberately small — 14px and a fade. A landing page that
 * flies its content in from the edges reads as a template; what this is for is
 * to stop a long page arriving as one flat wall.
 */
/**
 * The landing page at one of its URLs (lib/i18n/paths.ts). `/` speaks the
 * visitor's language; `/ru`, `/az`… speak their own, whatever the stored
 * preference — each language has a page a search engine can index. Showing a
 * page's language is not choosing it: only the language menu remembers (and the
 * footer's language links, which are the same menu).
 *
 * One component for every path, so moving between them (the language menu
 * navigates) keeps the page mounted instead of replaying its entrance.
 */
export function LandingPage({
  lang,
  onLocaleShow,
}: {
  lang: LocaleCode | null
  onLocaleShow: (locale: LocaleCode) => void
}) {
  const { locale } = useLocale()
  useEffect(() => {
    if (lang && lang !== locale) onLocaleShow(lang)
  }, [lang, locale, onLocaleShow])
  return <Landing />
}

const reveal = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.5, ease: [0.23, 1, 0.32, 1] as const },
}

export function Landing() {
  const t = useT()
  const { locale, setLocale } = useLocale()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [starting, setStarting] = useState(false)
  const reduceMotion = useReducedMotion()
  // Over static markup — the build rendering this page into index.html, or the
  // first mount over a page the visitor is already reading — nothing may fade
  // in a second time: the page renders settled, as under reduced motion.
  const [arrivedStatic] = useState(claimStaticLanding)
  const still = reduceMotion || arrivedStatic

  const { creating, create } = useCreatePlan()
  const [choosing, setChoosing] = useState(false)
  const [chooseError, setChooseError] = useState<string | null>(null)

  /**
   * Someone with plans here is coming back to them; someone without is asked
   * what they are planning. A browser that refuses storage lands on the
   * dashboard, which says so in words.
   */
  const start = async () => {
    setStarting(true)
    try {
      const plans = await api.listPlans()
      if (plans.length > 0) {
        navigate('/plans')
        return
      }
      setChooseError(null)
      setChoosing(true)
    } catch {
      navigate('/plans')
    } finally {
      setStarting(false)
    }
  }

  /** An occasion card or the chooser: either way the kind is decided. */
  const choose = (kind: EventKind) => {
    setChooseError(null)
    create(kind).catch((err: unknown) => {
      setChooseError(messageForError(t, err, t.errors.createPlan))
      setChoosing(true)
    })
  }

  // The tab follows the language; the static page arrives with it already set.
  useEffect(() => {
    document.title = t.landing.pageTitle
  }, [t])

  /** The language menu here moves to that language's page, and remembers the choice as it always has. */
  const toLanguagePage = (next: LocaleCode) => {
    const target = landingPath(next)
    if (target !== pathname) navigate(target)
  }

  /**
   * The footer's languages are the same menu written as links, because a
   * crawler follows an `href` and never opens a `<select>`. A plain click
   * chooses, as the menu does — `/` would otherwise keep the language it was
   * showing. A click for a new tab is the browser's.
   */
  const chooseLanguageLink = (next: LocaleCode) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    setLocale(next)
    toLanguagePage(next)
  }

  const motionProps = still ? {} : reveal

  return (
    <div className="landing">
      <header className="landing-nav">
        <div className="landing-nav-inner">
          <a href="#top" className="brand">
            <BrandMark />
            <span className="brand-name">Yerbəyer</span>
          </a>

          <nav className="landing-nav-links">
            <a href="#occasions">{t.landing.navOccasions}</a>
            <a href="#how">{t.landing.howItWorks}</a>
            <a href="#features">{t.landing.navFeatures}</a>
            <a href="#pdf">{t.landing.navPdf}</a>
            <a href="#free">{t.landing.navFree}</a>
          </nav>

          <div className="landing-nav-actions">
            <ThemeSwitch />
            <LanguagePicker onChange={toLanguagePage} />
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => navigate('/plans')}
            >
              {t.common.myPlans}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => void start()}
              disabled={starting}
            >
              {t.landing.startFree}
            </button>
          </div>
        </div>
      </header>

      {/* --- hero ------------------------------------------------------- */}
      <section className="hero" id="top">
        <motion.div
          className="hero-copy"
          initial={still ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.23, 1, 0.32, 1] }}
        >
          <p className="hero-eyebrow">
            <span className="hero-eyebrow-dot" />
            {t.landing.eyebrow}
          </p>

          <h1 className="hero-title">
            {t.landing.titleLead} <em>{t.landing.titleEmphasis}</em>.
          </h1>

          <p className="hero-lede">{t.landing.lede}</p>

          <div className="hero-actions">
            <button
              type="button"
              className="btn btn-primary btn-lg"
              onClick={() => void start()}
              disabled={starting}
            >
              {starting ? t.landing.opening : t.landing.startPlanningFree}
            </button>
          </div>

          <ul className="hero-points">
            {t.landing.heroPoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          className="hero-canvas"
          initial={still ? false : { opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.08, ease: [0.23, 1, 0.32, 1] }}
        >
          <MiniPlan still={still} />
          <span className="hero-badge">
            <span className="dot" />
            {t.landing.allChangesSaved}
          </span>
          <span className="hero-faces" aria-hidden="true">
            <i style={{ background: '#C9DCEE' }} />
            <i style={{ background: '#EED4DD' }} />
            <i style={{ background: '#EFD9C2' }} />
          </span>
        </motion.div>
      </section>

      {/* --- occasions --------------------------------------------------- */}
      {/* Four rooms, each drawn from the template it opens on. The wedding card
          is the wide one and carries the section's one dark button: weddings
          are where this started and still the first thing it is for. */}
      <section className="section occasions" id="occasions">
        <motion.div className="occasions-head" {...motionProps}>
          <span className="eyebrow eyebrow--lg">{t.landing.occasionsEyebrow}</span>
          <h2 className="section-title">{t.landing.occasionsTitle}</h2>
          <p className="section-lede">{t.landing.occasionsLede}</p>
        </motion.div>

        <div className="occasion-grid">
          {EVENT_KINDS.map((kind, index) => {
            const words = t.events.kinds[kind]
            return (
              <motion.article
                key={kind}
                className={`occasion-card occasion-card--${kind}`}
                {...motionProps}
                transition={{ ...reveal.transition, delay: index * 0.06 }}
              >
                <div className="occasion-room">
                  <RoomThumb kind={kind} />
                </div>
                <h3>{words.name}</h3>
                <p>{words.blurb}</p>
                <button
                  type="button"
                  className={`btn btn-sm occasion-cta ${kind === 'wedding' ? 'btn-primary' : 'btn-outline'}`}
                  data-kind={kind}
                  onClick={() => choose(kind)}
                  disabled={creating !== null}
                >
                  {creating === kind ? t.landing.opening : words.start}
                </button>
              </motion.article>
            )
          })}
        </div>
      </section>

      {/* --- how it works ----------------------------------------------- */}
      <section className="band" id="how">
        <div className="band-inner">
          <motion.div className="section-head" {...motionProps}>
            <div>
              <span className="eyebrow eyebrow--lg">{t.landing.howEyebrow}</span>
              <h2 className="section-title">{t.landing.howTitle}</h2>
            </div>
            <a href="#free" className="section-link">
              {t.landing.howCta} <span aria-hidden="true">→</span>
            </a>
          </motion.div>

          <div className="step-grid">
            {t.landing.steps.map((step, index) => (
              <motion.article
                key={step.title}
                className="step-card"
                {...motionProps}
                transition={{ ...reveal.transition, delay: index * 0.06 }}
              >
                <div className="step-card-head">
                  <span className="mono step-index">{String(index + 1).padStart(2, '0')}</span>
                  <span className={`step-swatch step-swatch--${index}`} />
                </div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* --- features ---------------------------------------------------- */}
      <section className="section" id="features">
        <motion.div {...motionProps}>
          <span className="eyebrow eyebrow--lg">{t.landing.featuresEyebrow}</span>
          <h2 className="section-title section-title--wide">{t.landing.featuresTitle}</h2>
        </motion.div>

        {/* One hairline table rather than six floating cards: the features are
            a list of facts, and a grid of shadowed boxes would give each of
            them the visual weight of a product. */}
        <motion.div className="feature-table" {...motionProps}>
          {t.landing.features.map((feature) => (
            <div className="feature-cell" key={feature.title}>
              <h3>{feature.title}</h3>
              <p>{feature.body}</p>
            </div>
          ))}
        </motion.div>
      </section>

      {/* --- the PDF ----------------------------------------------------- */}
      <section className="band" id="pdf">
        <div className="band-inner pdf-split">
          <motion.div {...motionProps}>
            <span className="eyebrow eyebrow--lg">{t.landing.pdfEyebrow}</span>
            <h2 className="section-title">{t.landing.pdfTitle}</h2>
            <p className="section-lede">{t.landing.pdfLede}</p>
            <ul className="tick-list">
              {t.landing.pdfPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </motion.div>

          <motion.div className="page-trio" {...motionProps} aria-hidden="true">
            {t.landing.pdfPageLabels.map((label, index) => (
              <div className="page-thumb" key={label}>
                <span className="page-sheet">
                  {index === 0 ? <FloorPlanPage /> : index === 1 ? <TableCardPage /> : <SeatIndexPage />}
                </span>
                <span className="mono page-caption">{label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* --- free ------------------------------------------------------- */}
      <section className="section" id="free">
        <motion.div className="pricing-head" {...motionProps}>
          <span className="eyebrow eyebrow--lg">{t.landing.freeEyebrow}</span>
          <h2 className="section-title section-title--center">{t.landing.freeTitle}</h2>
          <p className="section-lede section-lede--center">{t.landing.freeLede}</p>
        </motion.div>

        <div className="price-grid price-grid--single">
          <motion.div className="price-card price-card--featured" {...motionProps}>
            <span className="eyebrow">{t.landing.everythingLabel}</span>
            <p className="price-figure">{t.landing.freeLabel}</p>
            <p className="price-note">{t.landing.freeNote}</p>
            <ul className="price-points">
              {t.landing.freePoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
            <button
              type="button"
              className="btn btn-primary price-cta"
              onClick={() => void start()}
              disabled={starting}
            >
              {t.landing.startPlanning}
            </button>
          </motion.div>
        </div>
      </section>

      {/* --- FAQ ---------------------------------------------------------- */}
      <section className="section faq-split">
        <motion.h2 className="section-title" {...motionProps}>
          {t.landing.faqTitle}
        </motion.h2>
        <motion.div className="faq-list" {...motionProps}>
          {t.landing.faq.map((item) => (
            <div className="faq-item" key={item.q}>
              <h3>{item.q}</h3>
              <p>{item.a}</p>
            </div>
          ))}
        </motion.div>
      </section>

      {/* --- contact ------------------------------------------------------ */}
      <section className="section contact-split" id="contact">
        <motion.h2 className="section-title" {...motionProps}>
          {t.landing.contactTitle}
        </motion.h2>
        <motion.div className="contact-body" {...motionProps}>
          <p>{t.landing.contactLede}</p>
          <a className="section-link contact-email" href={`mailto:${CONTACT_EMAIL}`} dir="ltr">
            {CONTACT_EMAIL}
          </a>
        </motion.div>
      </section>

      {/* --- closing ------------------------------------------------------ */}
      <section className="closing">
        <motion.div className="closing-inner" {...motionProps}>
          <h2 className="closing-title">{t.landing.ctaTitle}</h2>
          <p className="closing-lede">{t.landing.ctaLede}</p>
          <button
            type="button"
            className="btn btn-invert btn-lg"
            onClick={() => void start()}
            disabled={starting}
          >
            {starting ? t.landing.opening : t.landing.startPlanningFree}
          </button>
        </motion.div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <span className="brand">
            <BrandMark />
            <span className="brand-name">Yerbəyer</span>
          </span>
          <nav className="landing-footer-links">
            <a href="#how">{t.landing.howItWorks}</a>
            <a href="#free">{t.landing.navFree}</a>
            <button type="button" onClick={() => navigate('/plans')}>
              {t.common.myPlans}
            </button>
            <a href={`mailto:${CONTACT_EMAIL}`}>{t.landing.navContact}</a>
          </nav>
          <span className="landing-footer-tagline">{t.landing.tagline}</span>
          <nav className="landing-footer-languages" aria-label={t.common.language}>
            {LOCALES.map((code) => (
              <a
                key={code}
                href={landingPath(code)}
                hrefLang={code}
                lang={code}
                aria-current={code === locale ? 'true' : undefined}
                onClick={chooseLanguageLink(code)}
              >
                {LOCALE_NAMES[code]}
              </a>
            ))}
          </nav>
        </div>
      </footer>

      <NewPlanDialog
        open={choosing}
        onClose={() => setChoosing(false)}
        onChoose={choose}
        creating={creating}
        error={chooseError}
      />
    </div>
  )
}

/* --- the three PDF pages, drawn at thumbnail size ------------------------- */
/*
 * Miniatures rather than screenshots. A screenshot of a real export would
 * either be illegible at 180px or would have to show somebody's actual guest
 * list; these carry the *shape* of each page — a room to scale, a card of
 * faces, two columns of names — which is the only thing the section is
 * claiming. They use the real table palette, so the pages on the marketing
 * page and the pages in the PDF are recognisably the same document.
 *
 * The viewBox is 100×130 to match the `aspect-ratio: 0.77` of the frame.
 */

const PAGE = { w: 100, h: 130 }
const INK = '#1C1D1F'

/** A ruled text line — the unit every one of these pages is built from. */
function Line({
  x, y, w, h = 2.2, o = 0.16, fill = INK,
}: { x: number; y: number; w: number; h?: number; o?: number; fill?: string }) {
  return <rect x={x} y={y} width={w} height={h} rx={h / 2} fill={fill} opacity={o} />
}

function Chair({ cx, cy, fill, stroke }: { cx: number; cy: number; fill: string; stroke: string }) {
  return <circle cx={cx} cy={cy} r={1.9} fill={fill} stroke={stroke} strokeWidth={0.6} />
}

/** A round table's chairs: evenly spaced on a ring from twelve o'clock. */
function ChairRing({ cx, cy, r, n, stroke, fill }: {
  cx: number; cy: number; r: number; n: number; stroke: string; fill: string
}) {
  return (
    <>
      {Array.from({ length: n }, (_, i) => {
        const a = (i / n) * 2 * Math.PI - Math.PI / 2
        return (
          <Chair
            key={i}
            cx={cx + Math.cos(a) * r}
            cy={cy + Math.sin(a) * r}
            fill={fill}
            stroke={stroke}
          />
        )
      })}
    </>
  )
}

/**
 * A square table's chairs sit *along its edges*, two a side — not on a ring.
 * The distinction is visible even at 90px: a ring puts chairs off the corners,
 * where at a real table nobody sits.
 */
function ChairEdges({ cx, cy, half, out, stroke, fill }: {
  cx: number; cy: number; half: number; out: number; stroke: string; fill: string
}) {
  const d = half / 2
  const o = half + out
  const points: Array<[number, number]> = [
    [cx - d, cy - o], [cx + d, cy - o],
    [cx + o, cy - d], [cx + o, cy + d],
    [cx + d, cy + o], [cx - d, cy + o],
    [cx - o, cy + d], [cx - o, cy - d],
  ]
  return (
    <>
      {points.map(([x, y], i) => (
        <Chair key={i} cx={x} cy={y} fill={fill} stroke={stroke} />
      ))}
    </>
  )
}

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <svg viewBox={`0 0 ${PAGE.w} ${PAGE.h}`} role="presentation">
      <rect width={PAGE.w} height={PAGE.h} fill="#FFFFFF" />
      {children}
    </svg>
  )
}

function FloorPlanPage() {
  return (
    <PageFrame>
      <Line x={9} y={9} w={38} h={3.4} o={0.62} />
      <Line x={9} y={16} w={22} h={2} o={0.2} />

      {/* the hall */}
      <rect x={8} y={24} width={84} height={97} rx={2.5} fill="#FBFAF8" stroke={INK} strokeOpacity={0.12} strokeWidth={0.8} />

      {/* sweetheart table at the head of the room */}
      <rect x={40} y={31} width={20} height={8} rx={2} fill="#F3E5EE" stroke="#C68CAF" strokeWidth={0.8} />
      <circle cx={46} cy={28} r={1.9} fill="#FBF4F8" stroke="#D9AEC6" strokeWidth={0.6} />
      <circle cx={54} cy={28} r={1.9} fill="#FBF4F8" stroke="#D9AEC6" strokeWidth={0.6} />

      {/* two rounds and two squares, the shape of an ordinary reception */}
      <circle cx={30} cy={62} r={8} fill="#E4EEF8" stroke="#7FA6D0" strokeWidth={0.8} />
      <ChairRing cx={30} cy={62} r={12} n={8} fill="#F4F8FC" stroke="#9DBBD9" />
      <circle cx={70} cy={62} r={8} fill="#E4EEF8" stroke="#7FA6D0" strokeWidth={0.8} />
      <ChairRing cx={70} cy={62} r={12} n={8} fill="#F4F8FC" stroke="#9DBBD9" />

      <rect x={22} y={92} width={16} height={16} rx={2} fill="#F7E8DE" stroke="#D0977B" strokeWidth={0.8} />
      <ChairEdges cx={30} cy={100} half={8} out={4} fill="#FDF6F1" stroke="#E0B69E" />
      <rect x={62} y={92} width={16} height={16} rx={2} fill="#F7E8DE" stroke="#D0977B" strokeWidth={0.8} />
      <ChairEdges cx={70} cy={100} half={8} out={4} fill="#FDF6F1" stroke="#E0B69E" />
    </PageFrame>
  )
}

function TableCardPage() {
  const rows = [
    '#E4EEF8', '#F3E5EE', '#F7E8DE', '#E4EEF8',
    '#F7E8DE', '#F3E5EE', '#E4EEF8', '#F7E8DE',
  ]
  return (
    <PageFrame>
      <Line x={9} y={10} w={30} h={4} o={0.62} />
      <Line x={9} y={18} w={44} h={2} o={0.2} />
      <rect x={9} y={25} width={82} height={0.7} fill={INK} opacity={0.14} />

      {rows.map((tint, i) => {
        const y = 33 + i * 11.4
        return (
          <g key={i}>
            {/* a face, then the name, then the seat number */}
            <circle cx={14} cy={y + 3} r={4} fill={tint} stroke={INK} strokeOpacity={0.1} strokeWidth={0.5} />
            <Line x={22} y={y} w={44 - (i % 3) * 7} h={2.6} o={0.34} />
            <Line x={22} y={y + 5} w={20 - (i % 2) * 5} h={2} o={0.14} />
            <Line x={84} y={y + 1.5} w={7} h={2.6} o={0.2} />
          </g>
        )
      })}
    </PageFrame>
  )
}

function SeatIndexPage() {
  const col = (x: number, seed: number) =>
    Array.from({ length: 11 }, (_, i) => {
      const y = 32 + i * 8.4
      return (
        <g key={`${x}-${i}`}>
          <Line x={x} y={y} w={22 - ((i + seed) % 4) * 3} h={2.3} o={0.32} />
          {/* the dotted leader that carries the eye across to the table number */}
          <line
            x1={x + 25} y1={y + 1.2} x2={x + 33} y2={y + 1.2}
            stroke={INK} strokeOpacity={0.18} strokeWidth={0.7} strokeDasharray="1 1.6"
          />
          <Line x={x + 35} y={y} w={4} h={2.3} o={0.42} />
        </g>
      )
    })

  return (
    <PageFrame>
      <Line x={9} y={10} w={40} h={4} o={0.62} />
      <Line x={9} y={18} w={26} h={2} o={0.2} />
      <rect x={9} y={25} width={82} height={0.7} fill={INK} opacity={0.14} />

      {col(9, 0)}
      {col(52, 2)}
      {/* the gutter between the two alphabetical columns */}
      <line x1={50} y1={31} x2={50} y2={122} stroke={INK} strokeOpacity={0.08} strokeWidth={0.7} />
    </PageFrame>
  )
}

/**
 * A small live floor plan, drawn with the same geometry module the editor and
 * the PDF use — so the shapes on the marketing page are the product's shapes,
 * not an illustrator's impression of them.
 */
function MiniPlan({ still }: { still: boolean }) {
  const t = useT()
  const { locale } = useLocale()
  const words = planWords(locale)

  const tables: Array<{
    shape: TableShape
    seats: number
    color: TableColor
    x: number
    y: number
    name: string
  }> = [
    { shape: 'sweetheart', seats: 2, color: 'pink', x: 0, y: -168, name: t.rail.sweetheartTable },
    { shape: 'round', seats: 10, color: 'blue', x: -158, y: 52, name: words.table(1) },
    { shape: 'round', seats: 8, color: 'blue', x: 158, y: 52, name: words.table(2) },
    { shape: 'square', seats: 8, color: 'orange', x: 0, y: 258, name: words.table(3) },
  ]

  return (
    <div className="mini-plan" aria-hidden="true">
      <div className="mini-plan-inner">
        {tables.map((table, tableIndex) => {
          const swatch = screenSwatchFor(table.color)
          const geometry = computeTableGeometry(table.shape, table.seats)
          return (
            <div key={table.name} className="mini-table" style={{ left: table.x, top: table.y }}>
              <motion.div
                className={`mini-top mini-top--${table.shape}`}
                style={{
                  width: geometry.width,
                  height: geometry.height,
                  marginLeft: -geometry.width / 2,
                  marginTop: -geometry.height / 2,
                  background: swatch.surface,
                  borderColor: swatch.edge,
                }}
                initial={still ? false : { opacity: 0, scale: 0.86 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  delay: 0.18 + tableIndex * 0.07,
                  type: 'spring',
                  duration: 0.6,
                  bounce: 0.2,
                }}
              />
              <span className="mini-name" style={{ color: swatch.ink }}>
                {table.name}
              </span>
              {/* Chairs spring outward from the table's centre into their ring —
                  the same motion the editor makes when a chair is added, so the
                  page is demonstrating the behaviour rather than decorating. */}
              {geometry.seats.map((seat, seatIndex) => (
                <motion.span
                  key={seat.index}
                  className="mini-seat"
                  style={{ background: swatch.seat, borderColor: swatch.seatEdge }}
                  initial={still ? false : { opacity: 0, x: 0, y: 0, scale: 0.5 }}
                  animate={{ opacity: 1, x: seat.x, y: seat.y, scale: 1 }}
                  transition={{
                    delay: 0.3 + tableIndex * 0.07 + seatIndex * 0.02,
                    type: 'spring',
                    duration: 0.55,
                    bounce: 0.22,
                  }}
                />
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}
